import React, { useMemo } from 'react';

import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useMarketingRouterRules } from '~/hooks/apis/promo/useMarketingRouterRules';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterInternalQuery } from '~/services/PromoService';

import { isoToSydneyDate } from '~/utils/dateFormatter';

import MarketingRouterRulesFilterBar from './MarketingRouterRulesFilterBar';
import MarketingRouterTimeline from './MarketingRouterTimeline';
import {
  EMPTY_MARKETING_ROUTER_RULE_FILTERS,
  filterMarketingRouterRules,
  hasActiveMarketingRouterRuleFilters,
  parseMarketingRouterRuleFilters,
  toMarketingRouterRuleFilterParams,
} from './marketingRouterRuleFilters';
import {
  TIMELINE_SCALE_OPTIONS,
  type TimelineScale,
  getTimelineWindow,
  isTimelineScale,
  shiftAnchor,
} from './marketingRouterTimelineLayout';

const SCALE_PARAM = 'scale';
const ANCHOR_PARAM = 'anchor';
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export default function MarketingRouterTimelinePage() {
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rules = rulesQuery.data ?? [];
  const sortedRules = useMemo(() => [...rules].sort((first, second) => first.id.localeCompare(second.id)), [rules]);

  // Filters, scale and anchor all live in the URL so a view can be shared and
  // survives a reload; the page holds no state of its own.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseMarketingRouterRuleFilters(searchParams), [searchParams]);
  const hasFilters = hasActiveMarketingRouterRuleFilters(filters);

  // Rule dates are Sydney calendar days, so "today" has to be one too.
  const today = useMemo(() => isoToSydneyDate(new Date().toISOString()), []);

  const scaleParam = searchParams.get(SCALE_PARAM) ?? '';
  const scale: TimelineScale = isTimelineScale(scaleParam) ? scaleParam : 'month';
  const anchorParam = searchParams.get(ANCHOR_PARAM) ?? '';
  const anchor = ISO_DAY.test(anchorParam) ? anchorParam : today;

  const window = useMemo(() => getTimelineWindow(anchor, scale), [anchor, scale]);
  const shownRules = useMemo(
    () => filterMarketingRouterRules(sortedRules, filters, today),
    [sortedRules, filters, today],
  );
  const regionCodes = useMemo(
    () =>
      getRegions(tenant.brand)
        .map((region) => region.code)
        .sort(),
    [tenant.brand],
  );

  const updateParams = (mutate: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  };

  const setScale = (nextScale: TimelineScale) => updateParams((params) => params.set(SCALE_PARAM, nextScale));
  const setAnchor = (nextAnchor: string) => updateParams((params) => params.set(ANCHOR_PARAM, nextAnchor));
  const goToday = () => updateParams((params) => params.delete(ANCHOR_PARAM));

  const resetFilters = () => {
    const next = toMarketingRouterRuleFilterParams(EMPTY_MARKETING_ROUTER_RULE_FILTERS);
    if (scale !== 'month') {
      next.set(SCALE_PARAM, scale);
    }
    if (anchorParam) {
      next.set(ANCHOR_PARAM, anchorParam);
    }
    setSearchParams(next, { replace: true });
  };
  const onFiltersChange = (nextFilters: typeof filters) =>
    updateParams((params) => {
      const applied = toMarketingRouterRuleFilterParams(nextFilters);
      // Set applied filters and clear any that were removed, leaving the scale
      // and anchor params untouched.
      for (const key of Object.keys(EMPTY_MARKETING_ROUTER_RULE_FILTERS)) {
        const value = applied.get(key);
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
    });

  const windowLabel =
    scale === 'month'
      ? dayjs(window.start).format('MMMM YYYY')
      : `${dayjs(window.start).format('DD MMM')} – ${dayjs(window.end).format('DD MMM YYYY')}`;

  return (
    <>
      <Helmet>
        <title>Marketing / Router Timeline</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Marketing Router Timeline">
          <Stack direction="row" spacing={1}>
            <Button component={Link} to="/marketing/marketing-router-rules" variant="text">
              Table view
            </Button>
            <Button component={Link} to="/marketing/marketing-router-rules/create" variant="contained" color="primary">
              Create rule
            </Button>
          </Stack>
        </PageHeader>
        <Card>
          <CardContent>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6">Timeline</Typography>
              <Typography color="text.secondary" variant="body2">
                Router rules for the current tenant across their schedule.
              </Typography>
            </Stack>

            <MarketingRouterRulesFilterBar
              filters={filters}
              onChange={onFiltersChange}
              onReset={resetFilters}
              regionCodes={regionCodes}
              shownCount={shownRules.length}
              totalCount={sortedRules.length}
            />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" size="small" onClick={goToday}>
                  Today
                </Button>
                <IconButton
                  size="small"
                  aria-label="Previous period"
                  onClick={() => setAnchor(shiftAnchor(anchor, scale, -1))}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Next period"
                  onClick={() => setAnchor(shiftAnchor(anchor, scale, 1))}
                >
                  <ChevronRightIcon />
                </IconButton>
                <Typography variant="subtitle1">{windowLabel}</Typography>
              </Stack>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={scale}
                onChange={(_event, next) => {
                  if (next) {
                    setScale(next as TimelineScale);
                  }
                }}
                aria-label="Timeline scale"
              >
                {TIMELINE_SCALE_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>

            {rulesQuery.isLoading ? <Typography color="text.secondary">Loading rules…</Typography> : null}
            {rulesQuery.isError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {rulesQuery.error instanceof Error
                  ? rulesQuery.error.message
                  : 'Failed to load marketing router rules.'}
              </Alert>
            ) : null}

            {!rulesQuery.isLoading &&
              (shownRules.length === 0 ? (
                <Typography color="text.secondary">
                  {hasFilters && sortedRules.length > 0
                    ? 'No rules match these filters.'
                    : 'No marketing router rules found.'}
                </Typography>
              ) : (
                <MarketingRouterTimeline rules={shownRules} today={today} window={window} />
              ))}
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
